import {
  parseProject,
  parseContext,
  type SourceParameters,
  type Project,
  type Values,
} from "@proof/core";
import { z } from "zod";

const prefix = "proof-impersonation-";
const handoffLifetime = 120_000;
const stateSchema = z.object({
  sourceParameters: z
    .unknown()
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : parseContext({ sourceParameters: value }).sourceParameters,
    ),
  project: z.unknown().transform(parseProject),
  values: z.record(
    z.string(),
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      z.record(z.string(), z.unknown()),
      z.null(),
    ]),
  ),
  partId: z.string(),
  previewSubject: z.string().min(1).max(200).nullable().optional(),
  demoProfile: z.enum(["alex", "jamie", "guest"]),
});
export interface ImpersonationState {
  sourceParameters?: SourceParameters;
  project: Project;
  values: Values;
  partId: string;
  previewSubject?: string | null;
  demoProfile: "alex" | "jamie" | "guest";
}

/** One-use same-origin handoff; the URL contains only an opaque identifier. */
export function prepareImpersonation(state: ImpersonationState): string {
  const now = Date.now();
  for (const key of Object.keys(localStorage)) {
    if (!key.startsWith(prefix)) continue;
    try {
      if (JSON.parse(localStorage.getItem(key)!).expires > now) continue;
    } catch {
      /* Remove abandoned or malformed handoffs. */
    }
    localStorage.removeItem(key);
  }
  // getRandomValues also works on HTTP LAN/computer-name origins.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  const id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  const key = prefix + id;
  localStorage.setItem(
    key,
    JSON.stringify({ state, expires: now + handoffLifetime }),
  );
  setTimeout(() => localStorage.removeItem(key), handoffLifetime);
  return `/impersonation#${id}`;
}

export function impersonationSessionKey(): string {
  const id = location.hash.slice(1);
  if (!/^[a-f0-9-]{36}$/.test(id))
    throw new Error("Invalid impersonation link.");
  return prefix + id;
}

export function readImpersonation(): ImpersonationState {
  const key = impersonationSessionKey();
  const existing = sessionStorage.getItem(key);
  if (existing) return stateSchema.parse(JSON.parse(existing));
  const handoff = JSON.parse(localStorage.getItem(key) || "null");
  if (!handoff || handoff.expires <= Date.now()) {
    localStorage.removeItem(key);
    throw new Error(
      "This impersonation link has expired. Open a new tab from Live proof.",
    );
  }
  const state = stateSchema.parse(handoff.state);
  sessionStorage.setItem(key, JSON.stringify(state));
  localStorage.removeItem(key);
  return state;
}
